import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import StatusBadge from "../components/StatusBadge";
import ReturnRowActions from "../components/ReturnRowActions";
import { Search, Plus, CheckCheck, Download } from "lucide-react";
import { fileUrl } from "../lib/api";
import { toast } from "sonner";

const STATUSES = ["Eingetroffen", "In Bearbeitung", "Erneut versendet", "Storniert", "Abgeschlossen"];

export default function Returns() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "all");
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const qs = {};
      if (search.trim()) qs.search = search.trim();
      if (statusFilter && statusFilter !== "all") qs.status_filter = statusFilter;
      const { data } = await api.get("/returns", { params: qs });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const tid = setTimeout(load, 200);
    return () => clearTimeout(tid);
    // eslint-disable-next-line
  }, [search, statusFilter]);

  useEffect(() => {
    if (statusFilter && statusFilter !== "all") setParams({ status: statusFilter });
    else setParams({});
    // eslint-disable-next-line
  }, [statusFilter]);

  const allChecked = useMemo(() => items.length > 0 && selected.size === items.length, [items, selected]);
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };
  const toggleOne = (id) => {
    const ns = new Set(selected);
    if (ns.has(id)) ns.delete(id);
    else ns.add(id);
    setSelected(ns);
  };

  const handleReturnChanged = (updated) => {
    setItems((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  };
  const handleReturnDeleted = (deletedId) => {
    setItems((prev) => prev.filter((r) => r.id !== deletedId));
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(deletedId);
      return next;
    });
  };

  const bulkComplete = async () => {
    if (selected.size === 0) return;
    try {
      await api.post("/returns/bulk-status", { ids: Array.from(selected), status: "Abgeschlossen" });
      toast.success(`${selected.size} ${t("common.saved")}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <p className="label-mini">{t("returns.title")}</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-none mt-1">{t("returns.title")}</h1>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button
              onClick={() => {
                const qs = new URLSearchParams();
                if (search.trim()) qs.set("search", search.trim());
                if (statusFilter && statusFilter !== "all") qs.set("status_filter", statusFilter);
                window.open(fileUrl(`/api/exports/returns.csv?${qs.toString()}`), "_blank");
              }}
              variant="outline"
              style={{ borderRadius: 4 }}
              data-testid="returns-export-button"
            >
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
          )}
          <Button
            onClick={() => navigate("/returns/new")}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
            style={{ borderRadius: 4 }}
            data-testid="returns-new-button"
          >
            <Plus className="w-4 h-4 mr-2" /> {t("returns.new")}
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t("returns.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            style={{ borderRadius: 4 }}
            data-testid="returns-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-56" data-testid="returns-status-filter" style={{ borderRadius: 4 }}>
            <SelectValue placeholder={t("returns.filter")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="filter-option-all">{t("returns.all")}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s} data-testid={`filter-option-${s}`}>
                {t(`stats.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-3 p-3 bg-[#E6EFFF] border border-[#B3CCFF]" style={{ borderRadius: 4 }} data-testid="returns-bulk-bar">
          <span className="text-sm font-medium">
            {selected.size} {t("returns.selected")}
          </span>
          <Button
            onClick={bulkComplete}
            className="bg-[#047857] hover:bg-[#036244] text-white ml-auto"
            size="sm"
            style={{ borderRadius: 4 }}
            data-testid="returns-bulk-complete-button"
          >
            <CheckCheck className="w-4 h-4 mr-2" /> {t("returns.bulk")}
          </Button>
        </div>
      )}

      {/* Desktop table */}
      <div className="swiss-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto table-scroll">
          <table className="w-full text-sm" data-testid="returns-table">
            <thead className="bg-[#F8F9FA] border-b border-border">
              <tr>
                <th className="px-3 py-3 text-left w-10">
                  <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="returns-select-all" />
                </th>
                <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">{t("returns.cols.id")}</th>
                <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">{t("returns.cols.order")}</th>
                <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">{t("returns.cols.customer")}</th>
                <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">{t("returns.cols.shop")}</th>
                <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">{t("returns.cols.status")}</th>
                <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">{t("returns.cols.created")}</th>
                <th className="px-3 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">{t("common.loading")}</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground" data-testid="returns-empty">{t("returns.empty")}</td></tr>
              )}
              {!loading && items.map((r, i) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/returns/${r.id}`)}
                  className="border-b border-border last:border-0 hover:bg-[#F8F9FA] fade-up cursor-pointer"
                  style={{ animationDelay: `${i * 20}ms` }}
                  data-testid={`return-row-${r.id}`}
                >
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(r.id)}
                      onCheckedChange={() => toggleOne(r.id)}
                      data-testid={`return-checkbox-${r.id}`}
                    />
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">
                    <Link to={`/returns/${r.id}`} className="text-[#0055FF] hover:underline" data-testid={`return-link-${r.id}`} onClick={(e) => e.stopPropagation()}>
                      {r.id}
                    </Link>
                  </td>
                  <td className="px-3 py-3">{r.order_number}</td>
                  <td className="px-3 py-3 font-medium">{r.customer_name}</td>
                  <td className="px-3 py-3 text-muted-foreground">{r.shop_origin || "—"}</td>
                  <td className="px-3 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-3 text-muted-foreground whitespace-nowrap text-xs">
                    {r.created_at && new Date(r.created_at).toLocaleDateString(lang === "de" ? "de-DE" : "en-US")}
                  </td>
                  <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <ReturnRowActions ret={r} onChanged={handleReturnChanged} onDeleted={handleReturnDeleted} variant="table" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="returns-mobile-list">
        {loading && (
          <div className="swiss-card p-6 text-center text-muted-foreground">{t("common.loading")}</div>
        )}
        {!loading && items.length === 0 && (
          <div className="swiss-card p-8 text-center text-muted-foreground" data-testid="returns-empty-mobile">{t("returns.empty")}</div>
        )}
        {!loading && items.map((r, i) => (
          <div
            key={r.id}
            className="swiss-card p-4 fade-up flex items-start gap-3"
            style={{ animationDelay: `${i * 20}ms` }}
            data-testid={`return-card-${r.id}`}
          >
            <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
              <Checkbox
                checked={selected.has(r.id)}
                onCheckedChange={() => toggleOne(r.id)}
                data-testid={`return-checkbox-mobile-${r.id}`}
              />
            </div>
            <button
              type="button"
              onClick={() => navigate(`/returns/${r.id}`)}
              className="flex-1 text-left min-w-0"
              data-testid={`return-open-mobile-${r.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-mono text-xs text-[#0055FF]">{r.id}</span>
                <StatusBadge status={r.status} />
              </div>
              <div className="font-semibold truncate">{r.customer_name}</div>
              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                <span>{t("returns.cols.order")}: {r.order_number}</span>
                {r.shop_origin && <span>· {r.shop_origin}</span>}
                {r.created_at && <span>· {new Date(r.created_at).toLocaleDateString(lang === "de" ? "de-DE" : "en-US")}</span>}
              </div>
            </button>
            <div onClick={(e) => e.stopPropagation()}>
              <ReturnRowActions ret={r} onChanged={handleReturnChanged} onDeleted={handleReturnDeleted} variant="card" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
